// MyMeetings.jsx — Employee side
// CRITICAL: MeetingCard is defined OUTSIDE MyMeetings to prevent cursor-losing remount bug

import { useState, useEffect } from 'react'
import { get, patch } from '../lib/api'

function fmtDate(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric', year:'numeric' })
}
function fmtTime(tm) {
  if (!tm) return ''
  const [h, m] = tm.split(':'), hr = parseInt(h)
  return `${hr > 12 ? hr - 12 : hr || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`
}
function isPast(d) { return d && new Date(d + 'T00:00:00') < new Date() }

const RSVP_STYLE = {
  pending:  { bg:'#FFFBEB', color:'#D97706', border:'#FDE68A', label:'⏳ Awaiting your response' },
  accepted: { bg:'#ECFDF5', color:'#059669', border:'#A7F3D0', label:'✅ Accepted'               },
  declined: { bg:'#FEF2F2', color:'#EF4444', border:'#FECACA', label:'✕ Declined'               },
}
const GCOLS = ['#4F46E5','#059669','#D97706','#7C3AED','#DB2777']
function gc(i) { return GCOLS[i % GCOLS.length] }

// ── MeetingCard — MUST be outside MyMeetings to avoid cursor bug ──────────
function MeetingCard({ m, profile, t, expandedId, setExpandedId, empMap,
                       progressDraft, setProgressDraft, savingProgress,
                       onRSVP, onToggleItem, onSaveProgress }) {
  const rsvp     = (m.rsvp_statuses || {})[profile.id] || 'pending'
  const rs       = RSVP_STYLE[rsvp]
  const isExp    = expandedId === m.id
  const isGroup  = m.meeting_type === 'group'
  const past     = isPast(m.meeting_date)

  // Attendees
  const attendees = isGroup
    ? (m.attendee_ids || []).map(id => empMap[id]).filter(Boolean)
    : m.employee_id ? [empMap[m.employee_id]].filter(Boolean) : []

  // My action items
  const myItems   = (m.structured_action_items || [])
    .map((item, idx) => ({ ...item, _idx: idx }))
    .filter(item => !item.assigned_to || item.assigned_to === profile.id)
  const doneCount = myItems.filter(i => i.completed).length
  const progress  = myItems.length ? Math.round((doneCount / myItems.length) * 100) : null

  const IS = { display:'block', width:'100%', padding:'9px 12px', background:t.input,
    border:`1px solid ${t.inputBorder}`, borderRadius:8, fontSize:13, color:t.inputText,
    fontFamily:'Inter,system-ui', boxSizing:'border-box', outline:'none', resize:'vertical' }

  return (
    <div style={{ background:t.card, border:`2px solid ${rsvp==='pending'?'#FDE68A':rsvp==='accepted'?'#A7F3D0':t.cardBorder}`, borderRadius:14, marginBottom:14, overflow:'hidden', boxShadow:t.shadow }}>

      {/* RSVP action banner */}
      {rsvp === 'pending' && (
        <div style={{ background:'linear-gradient(135deg,#FFFBEB,#FEF3C7)', borderBottom:'1px solid #FDE68A', padding:'10px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:13, fontWeight:600, color:'#D97706' }}>
            ⏳ Your manager invited you — please respond
          </span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={()=>onRSVP(m,'accepted')}
              style={{ padding:'6px 16px', background:'#059669', border:'none', borderRadius:7, fontSize:13, fontWeight:700, color:'#fff', cursor:'pointer', fontFamily:'Inter,system-ui' }}>
              ✓ Accept
            </button>
            <button onClick={()=>onRSVP(m,'declined')}
              style={{ padding:'6px 14px', background:'transparent', border:'1px solid #FECACA', borderRadius:7, fontSize:13, fontWeight:600, color:'#EF4444', cursor:'pointer', fontFamily:'Inter,system-ui' }}>
              ✕ Decline
            </button>
          </div>
        </div>
      )}

      {/* Card header */}
      <div style={{ padding:'1.25rem 1.5rem', cursor:'pointer', background:t.dark?'rgba(99,102,241,0.05)':'linear-gradient(135deg,#F8F9FF,#F4F4FF)' }}
        onClick={()=>setExpandedId(isExp ? null : m.id)}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:18, fontWeight:800, color:t.text, marginBottom:5, letterSpacing:'-0.3px' }}>
              {m.title || '1:1 Meeting'}
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginBottom:8 }}>
              <span style={{ fontSize:13, color:'#4F46E5', fontWeight:600 }}>
                📅 {fmtDate(m.meeting_date)}{m.meeting_time && ` · ${fmtTime(m.meeting_time)}`}
              </span>
              <span style={{ fontSize:11, fontWeight:700, padding:'2px 9px', borderRadius:99,
                background:isGroup?'#EFF6FF':'#F0F0FF', color:isGroup?'#3B82F6':'#4F46E5',
                border:`1px solid ${isGroup?'#BFDBFE':'#C7D2FE'}` }}>
                {isGroup?'👥 Group':'👤 Individual'}
              </span>
              {past && rsvp==='accepted' && (
                <span style={{ fontSize:11, color:t.textMuted, background:t.bg, padding:'2px 8px', borderRadius:99 }}>Past</span>
              )}
            </div>

            {/* Attendees — prominent */}
            {attendees.length > 0 && (
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                <span style={{ fontSize:11, color:t.textMuted, alignSelf:'center' }}>With:</span>
                {attendees.map((e,i) => (
                  <div key={e.id} style={{ display:'flex', alignItems:'center', gap:5, background:`${gc(i)}14`, border:`1px solid ${gc(i)}30`, borderRadius:99, padding:'3px 10px' }}>
                    <div style={{ width:18, height:18, borderRadius:'50%', background:gc(i), display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:900, color:'#fff' }}>
                      {e.full_name?.charAt(0)}
                    </div>
                    <span style={{ fontSize:12, fontWeight:700, color:gc(i) }}>{e.full_name}</span>
                    {e.position && <span style={{ fontSize:10, color:t.textMuted }}>· {e.position}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0, marginLeft:12 }}>
            <span style={{ fontSize:11, fontWeight:700, padding:'4px 12px', borderRadius:99, background:rs.bg, color:rs.color, border:`1px solid ${rs.border}` }}>
              {rs.label}
            </span>
            <span style={{ fontSize:13, color:t.textMuted }}>{isExp?'▲':'▼'}</span>
          </div>
        </div>

        {/* Action items progress bar */}
        {rsvp==='accepted' && myItems.length > 0 && (
          <div style={{ marginTop:8 }}>
            <div style={{ background:t.border, borderRadius:99, height:5, overflow:'hidden', marginBottom:3 }}>
              <div style={{ width:`${progress}%`, height:'100%', background:progress===100?'#059669':'linear-gradient(90deg,#4F46E5,#6366F1)', borderRadius:99 }} />
            </div>
            <div style={{ fontSize:11, color:t.textMuted }}>{doneCount}/{myItems.length} action items completed</div>
          </div>
        )}
      </div>

      {/* Expanded body */}
      {isExp && (
        <div style={{ padding:'1.25rem 1.5rem', borderTop:`1px solid ${t.border}` }}>

          {(m.agenda || m.notes) && (
            <div style={{ display:'grid', gridTemplateColumns:m.agenda&&m.notes?'1fr 1fr':'1fr', gap:14, marginBottom:'1.25rem' }}>
              {m.agenda && (
                <div style={{ background:t.dark?'rgba(255,255,255,0.04)':'#F8F9FA', borderRadius:10, padding:'10px 14px' }}>
                  <div style={{ fontSize:10, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:6 }}>Agenda</div>
                  <p style={{ fontSize:13, color:t.text, margin:0, lineHeight:1.7 }}>{m.agenda}</p>
                </div>
              )}
              {m.notes && (
                <div style={{ background:t.dark?'rgba(255,255,255,0.04)':'#F8F9FA', borderRadius:10, padding:'10px 14px' }}>
                  <div style={{ fontSize:10, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:6 }}>Notes</div>
                  <p style={{ fontSize:13, color:t.text, margin:0, lineHeight:1.7 }}>{m.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* My action items */}
          {rsvp === 'accepted' && (
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:t.text, marginBottom:10 }}>
                Your Action Items
                {myItems.length > 0 && <span style={{ fontSize:11, fontWeight:400, color:t.textMuted, marginLeft:8 }}>{doneCount}/{myItems.length} done</span>}
              </div>

              {myItems.length === 0 ? (
                <p style={{ fontSize:13, color:t.textMuted, fontStyle:'italic' }}>No action items assigned to you.</p>
              ) : myItems.map(item => {
                const existingNote = (m.item_progress_notes || {})[item.id]
                const draft        = progressDraft[item.id]
                const overdue      = !item.completed && item.due_date && isPast(item.due_date)

                return (
                  <div key={item.id} style={{ background:t.dark?'rgba(255,255,255,0.03)':'#F8F9FA', border:`1px solid ${item.completed?'#A7F3D0':overdue?'#FECACA':t.border}`, borderRadius:10, padding:'12px 14px', marginBottom:10 }}>
                    <div style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:8 }}>
                      <div onClick={()=>onToggleItem(m, item._idx)}
                        style={{ width:22, height:22, borderRadius:6, border:`2px solid ${item.completed?'#059669':overdue?'#EF4444':'#4F46E5'}`, background:item.completed?'#059669':'transparent', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, marginTop:1 }}>
                        {item.completed && <span style={{ fontSize:12, color:'#fff', fontWeight:900 }}>✓</span>}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:14, fontWeight:600, color:item.completed?t.textMuted:t.text, textDecoration:item.completed?'line-through':'none' }}>
                          {item.title}
                        </div>
                        {item.due_date && (
                          <div style={{ fontSize:11, fontWeight:600, color:overdue?'#EF4444':t.textMuted, marginTop:2 }}>
                            📅 Due {new Date(item.due_date+'T00:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                            {overdue && ' — Overdue'}
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:99, flexShrink:0,
                        background:item.completed?'#ECFDF5':overdue?'#FEF2F2':'#EFF6FF',
                        color:item.completed?'#059669':overdue?'#EF4444':'#4F46E5' }}>
                        {item.completed?'Done':overdue?'Overdue':'Pending'}
                      </span>
                    </div>

                    {/* Existing progress note */}
                    {existingNote && draft === undefined && (
                      <div style={{ background:'#EEF2FF', borderRadius:8, padding:'8px 10px', marginBottom:6 }}>
                        <div style={{ fontSize:10, fontWeight:700, color:'#4F46E5', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:3 }}>Your progress note</div>
                        <p style={{ fontSize:12, color:t.text, margin:0, lineHeight:1.6 }}>{existingNote}</p>
                      </div>
                    )}

                    {/* Draft / save note */}
                    {draft !== undefined ? (
                      <div>
                        <textarea
                          value={draft}
                          onChange={e => setProgressDraft(prev => ({ ...prev, [item.id]: e.target.value }))}
                          placeholder="Describe your progress on this item…"
                          rows={2}
                          style={{ ...IS, marginBottom:8 }}
                        />
                        <div style={{ display:'flex', gap:8 }}>
                          <button
                            onClick={() => onSaveProgress(m, item.id)}
                            disabled={savingProgress === item.id}
                            style={{ padding:'6px 14px', background:'linear-gradient(135deg,#4F46E5,#6366F1)', border:'none', borderRadius:7, fontSize:12, fontWeight:700, color:'#fff', cursor:'pointer', fontFamily:'Inter,system-ui' }}>
                            {savingProgress === item.id ? 'Saving…' : 'Save note'}
                          </button>
                          <button
                            onClick={() => setProgressDraft(prev => { const n={...prev}; delete n[item.id]; return n })}
                            style={{ padding:'6px 12px', background:'transparent', border:`1px solid ${t.border}`, borderRadius:7, fontSize:12, color:t.textSub, cursor:'pointer', fontFamily:'Inter,system-ui' }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setProgressDraft(prev => ({ ...prev, [item.id]: existingNote || '' }))}
                        style={{ fontSize:12, fontWeight:600, color:'#4F46E5', background:'transparent', border:'none', cursor:'pointer', padding:'4px 0', fontFamily:'Inter,system-ui' }}>
                        {existingNote ? '✏️ Edit progress note' : '+ Add progress note'}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {rsvp === 'declined' && (
            <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 14px', display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:13, color:'#DC2626', flex:1 }}>You declined this meeting.</span>
              <button onClick={()=>onRSVP(m,'accepted')}
                style={{ padding:'6px 14px', background:'#059669', border:'none', borderRadius:7, fontSize:12, fontWeight:700, color:'#fff', cursor:'pointer', fontFamily:'Inter,system-ui' }}>
                Accept instead
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// ROOT — MyMeetings
// ════════════════════════════════════════════════════════════════════════════
export default function MyMeetings({ t, profile, onRefresh: parentRefresh }) {
  const [meetings, setMeetings]       = useState([])
  const [employees, setEmployees]     = useState([])
  const [loading, setLoading]         = useState(true)
  const [expandedId, setExpandedId]   = useState(null)
  const [progressDraft, setProgressDraft] = useState({})
  const [savingProgress, setSavingProgress] = useState(null)

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    setLoading(true)
    try {
      const [mtgRes, empRes] = await Promise.all([
        get('/meetings'),  // includes direct + group meetings the employee attends
        get('/users'),
      ])
      setMeetings(mtgRes.meetings || [])
      setEmployees(empRes.users || [])
    } catch (e) { console.error(e.message) }
    setLoading(false)
  }

  const empMap = Object.fromEntries(employees.map(e => [e.id, e]))

  async function handleRSVP(m, status) {
    const updated = { ...(m.rsvp_statuses || {}), [profile.id]: status }
    await patch(`/meetings/${m.id}`, { rsvp_statuses: updated })
    fetchAll(); if (parentRefresh) parentRefresh()
  }

  async function handleToggleItem(m, idx) {
    const items = [...(m.structured_action_items || [])]
    items[idx] = { ...items[idx], completed: !items[idx].completed }
    await patch(`/meetings/${m.id}`, { structured_action_items: items })
    fetchAll(); if (parentRefresh) parentRefresh()
  }

  async function handleSaveProgress(m, itemId) {
    const note = (progressDraft[itemId] || '').trim()
    if (!note) return
    setSavingProgress(itemId)
    const notes = { ...(m.item_progress_notes || {}), [itemId]: note }
    await patch(`/meetings/${m.id}`, { item_progress_notes: notes })
    setProgressDraft(prev => { const n = {...prev}; delete n[itemId]; return n })
    setSavingProgress(null)
    fetchAll(); if (parentRefresh) parentRefresh()
  }

  function getMyRSVP(m) { return (m.rsvp_statuses || {})[profile.id] || 'pending' }

  const pending  = meetings.filter(m => getMyRSVP(m) === 'pending')
  const accepted = meetings.filter(m => getMyRSVP(m) === 'accepted')
  const declined = meetings.filter(m => getMyRSVP(m) === 'declined')

  const cardProps = { profile, t, expandedId, setExpandedId, empMap, progressDraft, setProgressDraft, savingProgress, onRSVP: handleRSVP, onToggleItem: handleToggleItem, onSaveProgress: handleSaveProgress }

  return (
    <div>
      <h1 style={{ fontSize:24, fontWeight:800, color:t.text, margin:'0 0 4px', letterSpacing:'-0.5px' }}>My Meetings</h1>
      <p style={{ fontSize:14, color:t.textMuted, margin:'0 0 1.5rem' }}>Meeting requests and action items from your manager</p>

      {loading ? <p style={{ color:t.textMuted }}>Loading…</p>
      : meetings.length === 0 ? (
        <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'4rem', textAlign:'center', boxShadow:t.shadow }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🗓</div>
          <h3 style={{ fontSize:16, fontWeight:700, color:t.text, marginBottom:8 }}>No meetings yet</h3>
          <p style={{ fontSize:14, color:t.textMuted }}>Your manager will schedule meetings here.</p>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <div style={{ marginBottom:'1.75rem' }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#D97706', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:10, display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ width:8, height:8, borderRadius:'50%', background:'#F59E0B', display:'inline-block' }} />
                Awaiting your response ({pending.length})
              </div>
              {pending.map(m => <MeetingCard key={m.id} m={m} {...cardProps} />)}
            </div>
          )}
          {accepted.length > 0 && (
            <div style={{ marginBottom:'1.75rem' }}>
              <div style={{ fontSize:13, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:10 }}>
                Accepted ({accepted.length})
              </div>
              {accepted.map(m => <MeetingCard key={m.id} m={m} {...cardProps} />)}
            </div>
          )}
          {declined.length > 0 && (
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:10 }}>
                Declined ({declined.length})
              </div>
              {declined.map(m => <MeetingCard key={m.id} m={m} {...cardProps} />)}
            </div>
          )}
        </>
      )}
    </div>
  )
}
